import { applyConsent, isAuthenticated } from './loader'

// Versioned so we can re-ask if the tag list changes materially rather than
// silently reusing consent given for a different set of tags. Bump the suffix
// (v1 -> v2) to invalidate every stored choice.
export const CONSENT_KEY = 'purposely_consent_v1'

// Stored as a cookie rather than in localStorage: logging out (and a few legacy
// auth paths) call window.localStorage.clear(), which would wipe the visitor's
// choice and re-prompt them. A cookie survives that, and keeps the choice
// readable server-side if we ever need to record or act on it there.
const CONSENT_MAX_AGE = 60 * 60 * 24 * 182 // ~6 months, per CNIL guidance

// Fired when something asks to (re)open the preferences UI — e.g. the footer /
// privacy-policy "Cookie preferences" links. The banner component listens.
export const OPEN_CONSENT_EVENT = 'purposely:open-consent'

const isBrowser = () => typeof window !== 'undefined'

const readCookie = (name) => {
  const match = document.cookie.match(
    new RegExp(`(?:^|; )${name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}=([^;]*)`)
  )
  return match ? decodeURIComponent(match[1]) : null
}

const writeCookie = (name, value) => {
  const secure = window.location.protocol === 'https:' ? '; Secure' : ''
  document.cookie = `${name}=${encodeURIComponent(
    value
  )}; Max-Age=${CONSENT_MAX_AGE}; Path=/; SameSite=Lax${secure}`
}

export const getConsent = () => {
  if (!isBrowser()) return null
  try {
    const raw = readCookie(CONSENT_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw)
    if (typeof parsed?.analytics !== 'boolean' || typeof parsed?.marketing !== 'boolean') return null
    return {
      analytics: !!parsed.analytics,
      marketing: !!parsed.marketing,
      ts: parsed.ts || null,
    }
  } catch {
    return null
  }
}

export const hasConsentDecision = () => getConsent() !== null

// Persists the choice and injects any newly permitted tags. Returns true when a
// reload is required: we can inject tags live, but we cannot cleanly unload GTM
// or Apollo once they have run, so revoking a previously granted category needs
// a page reload to take effect.
export const setConsent = ({ analytics, marketing }) => {
  if (!isBrowser()) return false
  const previous = getConsent()
  const value = { analytics: !!analytics, marketing: !!marketing, ts: new Date().toISOString() }
  writeCookie(CONSENT_KEY, JSON.stringify(value))

  const revoked =
    !!previous &&
    ((previous.analytics && !value.analytics) || (previous.marketing && !value.marketing))

  applyConsent(value)
  return revoked
}

// Call once on app bootstrap: re-injects whatever the stored choice permits.
export const applyStoredConsent = () => {
  const consent = getConsent()
  if (consent) applyConsent(consent)
}

export const openConsentPreferences = () => {
  if (isBrowser()) window.dispatchEvent(new CustomEvent(OPEN_CONSENT_EVENT))
}

export { isAuthenticated }
export { hasLoadedTracking } from './loader'
